// API Service para comunicação com o backend

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiService {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/api`;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Extrair headers e timeout do options para mesclar corretamente
    const { headers: optionHeaders, timeout = 30000, ...restOptions } = options;
    
    // Criar AbortController para timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const config = {
      ...restOptions,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...optionHeaders,
      },
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Erro desconhecido' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      // Tratar erro de abort como timeout
      if (error.name === 'AbortError') {
        console.error(`API Timeout [${endpoint}]: Request took longer than ${timeout}ms`);
        throw new Error('Timeout: A requisição demorou demais. Seus dados foram salvos localmente.');
      }
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // ==================== REPORTS ====================
  
  async getReports(status = null) {
    const query = status ? `?status=${status}` : '';
    return this.request(`/reports${query}`);
  }

  async getReport(reportId) {
    return this.request(`/reports/${reportId}`);
  }

  async getReportByEmail(email) {
    return this.request(`/reports/by-email/${encodeURIComponent(email)}`);
  }

  async createReport(data) {
    return this.request('/reports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReport(reportId, data) {
    return this.request(`/reports/${reportId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteReport(reportId) {
    return this.request(`/reports/${reportId}`, {
      method: 'DELETE',
    });
  }

  // ==================== INITIAL FORM ====================
  
  async updateInitialForm(reportId, formData) {
    // Converter nomes de campos do frontend para o backend (camelCase -> snake_case)
    const backendFormData = {
      sleep_difficulty: formData.sleepDifficulty,
      expectations: formData.expectations,
      previous_attempts: formData.previousAttempts,
      health_conditions: formData.healthConditions,
      heel_prick_test: formData.heelPrickTest,
      snoring: formData.snoring,
      iron_supplementation: formData.ironSupplementation,
      feeding_type: formData.feedingType,
      solid_foods: formData.solidFoods,
      motor_development: formData.motorDevelopment,
      how_baby_sleeps: formData.howBabySleeps,
      sleep_location: formData.sleepLocation,
      transition_object: formData.transitionObject === 'yes' || formData.transitionObject === true,
      transition_object_desc: formData.transitionObjectDesc,
      caregiver: formData.caregiver,
      other_children: formData.otherChildren,
      mother_sleep: formData.motherSleep,
    };

    return this.request(`/reports/${reportId}/initial-form`, {
      method: 'PUT',
      body: JSON.stringify({ form_data: backendFormData }),
    });
  }

  // Atualizar dados básicos do relatório (nome do bebê, mãe, etc)
  async updateReportBasicInfo(reportId, data) {
    const backendData = {
      baby_name: data.babyName,
      baby_age: data.babyAge ? parseInt(data.babyAge) : null,
      birth_weeks: data.birthWeeks ? parseInt(data.birthWeeks) : null,
      birth_date: data.birthDate,
      mother_name: data.motherName,
      father_name: data.fatherName,
      email: data.email,
      whatsapp: data.whatsapp,
    };

    // Remover campos undefined/null
    Object.keys(backendData).forEach(key => {
      if (backendData[key] === undefined || backendData[key] === null || backendData[key] === '') {
        delete backendData[key];
      }
    });

    return this.request(`/reports/${reportId}`, {
      method: 'PUT',
      body: JSON.stringify(backendData),
    });
  }

  // ==================== ROUTINE DAYS ====================
  
  async updateRoutineDay(reportId, dayNumber, dayData) {
    // Converter dados do frontend para o backend
    const backendDayData = {
      day: dayNumber,
      date: dayData.date,
      completed: true,
      wake_up_time: dayData.wakeUpTime,
      bed_time: dayData.bedTime,
      night_wakings: dayData.nightWakings || null,
      night_wakings_duration: dayData.nightWakingsDuration || null,
      naps: (dayData.naps || []).filter(nap => nap.startTime || nap.duration || nap.howSlept || nap.whereSlept).map(nap => ({
        start_time: nap.startTime || null,
        duration: nap.duration || null,  // Manter como string (ex: "2 horas", "30 minutos")
        time_to_sleep: nap.timeToSleep || null,  // Manter como string (ex: "10-15 minutos")
        how_slept: nap.howSlept || null,
        where_slept: nap.whereSlept || null,
      })),
      routine_entries: (dayData.routineEntries || []).map(entry => ({
        time: entry.time,
        activity: entry.customActivity || entry.activity,
      })),
      routine_description: dayData.routineDescription || this.buildRoutineDescription(dayData.routineEntries),
      important_notes: dayData.importantNotes,
    };

    return this.request(`/reports/${reportId}/routine/${dayNumber}`, {
      method: 'PUT',
      body: JSON.stringify({ day_data: backendDayData }),
      timeout: 15000, // 15 segundos para salvar rotina
    });
  }

  // Construir descrição da rotina a partir das entradas estruturadas
  buildRoutineDescription(entries) {
    if (!entries || entries.length === 0) return '';
    return entries
      .filter(e => e.time && (e.activity || e.customActivity))
      .map(e => `${e.time} - ${e.customActivity || e.activity}`)
      .join('. ');
  }

  // ==================== SLEEP PLAN ====================
  
  async updateSleepPlan(reportId, planData) {
    return this.request(`/reports/${reportId}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ plan_data: planData }),
    });
  }

  async approvePlan(reportId) {
    return this.request(`/reports/${reportId}/approve`, {
      method: 'POST',
    });
  }

  // ==================== DRAFTS ====================
  
  async createDraft(data) {
    return this.request('/reports/draft', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDraft(reportId, data) {
    return this.request(`/reports/${reportId}/draft`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getDrafts() {
    return this.request('/reports/drafts');
  }

  // ==================== TRAINING MATERIALS ====================
  
  async getTrainingMaterials(category = null) {
    const query = category ? `?category=${category}` : '';
    return this.request(`/training-materials${query}`);
  }

  async getTrainingMaterial(materialId) {
    return this.request(`/training-materials/${materialId}`);
  }

  async createTrainingMaterial(data) {
    return this.request('/training-materials', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTrainingMaterial(materialId, data) {
    return this.request(`/training-materials/${materialId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTrainingMaterial(materialId) {
    return this.request(`/training-materials/${materialId}`, {
      method: 'DELETE',
    });
  }

  async getTrainingStats() {
    return this.request('/training-materials/stats/summary');
  }

  // ==================== PLAN VERSIONS ====================
  
  async getPlanVersions(reportId) {
    return this.request(`/reports/${reportId}/versions`);
  }

  async savePlanVersion(reportId, note = null) {
    return this.request(`/reports/${reportId}/versions`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  async getPlanVersion(reportId, versionId) {
    return this.request(`/reports/${reportId}/versions/${versionId}`);
  }

  async restorePlanVersion(reportId, versionId) {
    return this.request(`/reports/${reportId}/versions/${versionId}/restore`, {
      method: 'POST',
    });
  }

  async deletePlanVersion(reportId, versionId) {
    return this.request(`/reports/${reportId}/versions/${versionId}`, {
      method: 'DELETE',
    });
  }

  // ==================== STATS ====================
  
  async getStats() {
    return this.request('/stats');
  }

  // ==================== TEMPLATES ====================
  
  async getTemplates() {
    return this.request('/templates', {
      headers: this.getAuthHeaders()
    });
  }

  async getTemplate(templateId) {
    return this.request(`/templates/${templateId}`, {
      headers: this.getAuthHeaders()
    });
  }

  async createTemplate(data) {
    return this.request('/templates', {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
  }

  async updateTemplate(templateId, data) {
    return this.request(`/templates/${templateId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
  }

  async deleteTemplate(templateId) {
    return this.request(`/templates/${templateId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
  }

  async applyTemplate(templateId, reportId) {
    return this.request(`/templates/${templateId}/apply/${reportId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
  }

  // Helper para obter headers de autenticação
  getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }
}

// Singleton instance
const apiService = new ApiService();
export default apiService;

// Helper para converter dados do backend para o frontend (snake_case -> camelCase)
export const convertReportToFrontend = (report) => {
  if (!report) return null;
  
  return {
    id: report.id,
    babyName: report.baby_name,
    babyAge: report.baby_age,
    birthWeeks: report.birth_weeks,
    birthDate: report.birth_date,
    motherName: report.mother_name,
    fatherName: report.father_name,
    email: report.email,
    whatsapp: report.whatsapp,
    status: report.status,
    createdAt: report.created_at,
    updatedAt: report.updated_at,
    completedDays: report.completed_days,
    initialForm: report.initial_form ? {
      sleepDifficulty: report.initial_form.sleep_difficulty,
      expectations: report.initial_form.expectations,
      previousAttempts: report.initial_form.previous_attempts,
      healthConditions: report.initial_form.health_conditions,
      heelPrickTest: report.initial_form.heel_prick_test,
      snoring: report.initial_form.snoring,
      ironSupplementation: report.initial_form.iron_supplementation,
      feedingType: report.initial_form.feeding_type,
      solidFoods: report.initial_form.solid_foods,
      motorDevelopment: report.initial_form.motor_development,
      howBabySleeps: report.initial_form.how_baby_sleeps,
      sleepLocation: report.initial_form.sleep_location,
      transitionObject: report.initial_form.transition_object,
      transitionObjectDesc: report.initial_form.transition_object_desc,
      caregiver: report.initial_form.caregiver,
      otherChildren: report.initial_form.other_children,
      motherSleep: report.initial_form.mother_sleep,
    } : null,
    routineDays: (report.routine_days || []).map(day => ({
      day: day.day_number || day.day,
      date: day.date,
      completed: day.status === 'complete' || day.completed,
      wakeUpTime: day.wake_up_time,
      bedTime: day.bed_time,
      nightWakings: day.night_wakings,
      nightWakingsDuration: day.night_wakings_duration,
      naps: (day.naps || []).map(nap => ({
        startTime: nap.start_time,
        duration: nap.duration,
        timeToSleep: nap.time_to_sleep,
        howSlept: nap.how_slept,
        whereSlept: nap.where_slept,
      })),
      routineDescription: day.routine_description,
      routineEntries: day.routine_entries || [],
      importantNotes: day.important_notes,
    })),
    sleepPlan: report.sleep_plan,
  };
};
